import { useQuery } from "@tanstack/react-query";
import {
  PARAMETERS,
  bypassStatusLabel,
  type LatestReading,
  type ParameterId,
} from "@tbm/shared";
import { api } from "../lib/api";
import { Sparkline } from "../components/Sparkline";
import { ShiftPanel } from "../components/ShiftPanel";
import { DrawingPanel } from "../components/DrawingPanel";
import { fmtNum } from "../lib/format";

interface DashboardProps {
  timezone: string;
}

const HERO_IDS: ParameterId[] = [
  "chainage",
  "advanceRate",
  "jackingForce",
  "facePressure",
];

interface RowGroup {
  title: string;
  ids: ParameterId[];
}

const ROW_GROUPS: RowGroup[] = [
  { title: "Pressures",  ids: ["feedLinePressure", "slurryLinePressure"] },
  { title: "Flows",      ids: ["feedFlowRate", "returnFlowRate"] },
  { title: "Cutterhead", ids: ["rpm", "torque", "totalScForce"] },
];

export function Dashboard({ timezone }: DashboardProps) {
  const latest = useQuery({
    queryKey: ["latest"],
    queryFn: api.latest,
    refetchInterval: 10_000,
  });
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });

  const byId: Partial<Record<ParameterId, LatestReading>> = {};
  for (const r of latest.data?.readings ?? []) byId[r.parameterId] = r;

  const liveChainage = byId.chainage?.value ?? null;
  const driveLengthM = drive.data?.lengthM ?? null;

  return (
    <div className="flex flex-col gap-4">
      {latest.data ? (
        <ShiftPanel
          shift={latest.data.shift}
          timezone={timezone}
          pipeMetrics={latest.data.pipeMetrics}
        />
      ) : (
        // Reserve the panel's height so the dashboard doesn't jump when
        // the first /api/latest response lands.
        <div className="card h-[88px]" />
      )}

      <div className="flex gap-4 items-stretch">
        <aside className="w-[320px] flex-shrink-0">
          <ControlsCard byId={byId} />
        </aside>

        <div className="flex-1 min-w-0">
          <DrawingPanel
            liveChainageM={liveChainage}
            driveLengthM={driveLengthM}
          />
        </div>
      </div>
    </div>
  );
}

function ControlsCard({
  byId,
}: {
  byId: Partial<Record<ParameterId, LatestReading>>;
}) {
  return (
    <section className="card flex flex-col h-full overflow-hidden">
      <header className="px-3 h-12 flex items-center border-b border-border1">
        <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase text-text">
          Live TBM parameters
        </h3>
      </header>

      <BypassRow value={byId.bypassStatus?.value ?? null} />

      <SectionDivider label="Drive" />
      <div className="grid grid-cols-2">
        {HERO_IDS.map((id, idx) => {
          const p = PARAMETERS[id];
          const r = byId[id];
          return (
            <HeroCell
              key={id}
              label={p.shortName}
              unit={p.unit}
              value={r?.value ?? null}
              precision={p.precision}
              spark={r?.spark ?? []}
              borderRight={idx % 2 === 0}
              borderBottom={idx < 2}
            />
          );
        })}
      </div>

      {ROW_GROUPS.map((g) => (
        <div key={g.title}>
          <SectionDivider label={g.title} />
          <ul>
            {g.ids.map((id, i) => {
              const p = PARAMETERS[id];
              const r = byId[id];
              return (
                <RowItem
                  key={id}
                  label={p.displayName}
                  unit={p.unit}
                  value={r?.value ?? null}
                  precision={p.precision}
                  isLast={i === g.ids.length - 1}
                />
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="px-3 pt-3 pb-1 border-t border-border1 flex items-center gap-2">
      <span className="text-[9.5px] uppercase tracking-[0.22em] text-dim font-semibold">
        {label}
      </span>
      <span className="flex-1 h-px bg-border1" />
    </div>
  );
}

function BypassRow({ value }: { value: number | null }) {
  const b = bypassStatusLabel(value);
  const dot =
    b.tone === "ok"
      ? "h-2.5 w-2.5 rounded-full bg-ok"
      : b.tone === "warn"
        ? "h-2.5 w-2.5 rounded-full bg-warn animate-pulseLive"
        : "h-2.5 w-2.5 rounded-full bg-wb animate-pulseLive";
  const text =
    b.tone === "ok"
      ? "text-ok"
      : b.tone === "warn"
        ? "text-warn"
        : "text-wb";
  return (
    <div className="px-3 py-2.5 flex items-center gap-3">
      <div className={dot} />
      <span className="text-[10.5px] uppercase tracking-[0.22em] text-dim flex-1">
        Bypass
      </span>
      <span className={`mono-nums font-semibold text-[13px] ${text}`}>{b.label}</span>
    </div>
  );
}

function HeroCell({
  label,
  unit,
  value,
  precision,
  spark,
  borderRight,
  borderBottom,
}: {
  label: string;
  unit: string;
  value: number | null;
  precision: number;
  spark: (number | null)[];
  borderRight: boolean;
  borderBottom: boolean;
}) {
  return (
    <div
      className={
        "px-3 py-2.5 relative " +
        (borderRight ? "border-r border-border1 " : "") +
        (borderBottom ? "border-b border-border1 " : "")
      }
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-dim">{label}</div>
        <div className="text-[9.5px] text-muted mono-nums">{unit}</div>
      </div>
      <div className="mono-nums text-[22px] font-semibold text-text leading-none mt-1.5">
        {fmtNum(value, precision)}
      </div>
      {/* Always reserve the sparkline's height so the cell doesn't grow when
          the first batch of values arrives. */}
      <div className="mt-1 -mx-1 h-[14px]">
        {spark.length >= 2 && <Sparkline values={spark} height={14} />}
      </div>
    </div>
  );
}

function RowItem({
  label,
  unit,
  value,
  precision,
  isLast,
}: {
  label: string;
  unit: string;
  value: number | null;
  precision: number;
  isLast: boolean;
}) {
  return (
    <li
      className={
        "px-3 py-1.5 flex items-baseline justify-between gap-3 " +
        (isLast ? "" : "border-b border-border1/60")
      }
    >
      <span className="text-[12px] text-muted truncate">{label}</span>
      <span className="mono-nums text-[13px] text-text font-medium whitespace-nowrap">
        {fmtNum(value, precision)}
        {unit && (
          <span className="text-[10px] text-muted font-normal ml-1">{unit}</span>
        )}
      </span>
    </li>
  );
}
